import type { GalleryStatusTone } from '../components/ItemGallery';
import type { PinnedApp } from '../hooks/useAppRegistryClient';

export type AppsFilter = 'all' | 'mine' | 'shared' | 'archived';

export type AppStatusBadge = { label: string; tone: GalleryStatusTone };

/**
 * Lifecycle pill for an app card, or ``undefined`` for a plain active app.
 *
 * Unpublished changes are only surfaced to viewers who can act on them
 * (publish requires write access), and carry a warning tone matching the
 * badge on the app view itself.
 */
export function appStatusBadge(app: PinnedApp): AppStatusBadge | undefined {
  const archived = app.status === 'archived' || app.archived_at != null;
  if (archived) return { label: 'Archived', tone: 'neutral' };
  if (app.can_write && app.has_unpublished_changes) return { label: 'Unpublished changes', tone: 'warning' };
  return undefined;
}

/**
 * Pure ownership/lifecycle filter+sort for the Apps gallery. Kept out of the
 * component so My/Shared/All/Archived logic is testable without rendering.
 *
 * - ``mine``: active apps the viewer owns.
 * - ``shared``: active apps the viewer can see but does not own.
 * - ``all``: every active app the viewer can read.
 * - ``archived``: soft-archived apps (loaded via ``lifecycle=archived``).
 *
 * Title/description search is handled by ItemGallery ``filterByQuery``.
 * Results are sorted by title.
 *
 * Callers should pass the matching list from the API (active vs archived);
 * this helper still filters by ``status`` so mixed lists stay correct.
 */
export function filterPinnedApps(apps: PinnedApp[], filter: AppsFilter): PinnedApp[] {
  return apps
    .filter((app) => {
      const archived = app.status === 'archived' || app.archived_at != null;
      if (filter === 'archived') {
        return archived;
      }
      if (archived) return false;
      if (filter === 'mine') return app.is_owner;
      if (filter === 'shared') return !app.is_owner;
      return true;
    })
    .sort((a, b) => a.title.localeCompare(b.title));
}

/** Active apps only — command palette must never surface archived apps. */
export function activePinnedAppsForPalette(apps: PinnedApp[]): PinnedApp[] {
  return apps.filter((app) => app.status !== 'archived' && app.archived_at == null);
}
